import './fb-grid.css';
import { parse } from '@edicom/common/parser/parse';

fbGridDirective.$inject = ['$compile', 'injectorService'];

export default function fbGridDirective($compile, injectorService) {
  return {
    restrict: 'E',
    scope: {
      fbFlash: '=',
    },
    require: '^^?ecFormBuilder',
    link,
  };

  function link(scope, element, attrs, ecFormBuilderController) {
    ecFormBuilderController = ecFormBuilderController || scope.ecFormBuilderController;

    // buscamos el ecFormBuilderController en los scope.$parent
    let tmp = scope;
    tmp.ecFormBuilderController = scope.ecFormBuilderController || ecFormBuilderController;
    while (!tmp.ecFormBuilderController && tmp) {
      tmp = tmp.$parent;
    }
    ecFormBuilderController = tmp.ecFormBuilderController;
    if (!ecFormBuilderController) {
      throw new Error(`ecFormBuilderController no encontrado en ${scope}`);
    }

    /**
     * binding del scope
     */

    // recuperamos el modelo padre para el binding
    // ej. si xPath = CAB.TEST.LIN -> CAB.TEST (binding de TEST en el scope)
    //   o si CAB.LIN -> CAB (binding de CAB en el scope)
    const parentRegister = attrs.xPath
      .split('.')
      .splice(0, attrs.xPath.split('.').length - 1)
      .join('.');

    // @odeniel - 20200715: reemplazado bindRegister por bind para que se copie CAB también en el scope.
    // eso resuelve el desabilitar grid dinamicamente cuando es mas profundo (=> CAB.LOQUESEA.GRID)
    // se puede probar ahi:
    // https://components-catalog.edicom.es/components/ec-form-builder-edicom/disabled/demo > sección "sección con grid profundo que se desabilita dinamicamente"
    // lo comento porque igual genera problemas en celdas o lo que sea.
    // ecFormBuilderController.bindRegister(scope, parentRegister);
    ecFormBuilderController.bind(scope, parentRegister);

    /**
     * Devuelve un scope bindeado, es decir, con los registros padres, ej: scope.CAB, scope.EMB, scope.LIN
     * -> se usa en parserFn
     * @param {string} path el modelo del campo
     */
    const bindScope = path => {
      const scopeTemporal = scope.$new(true, scope);
      ecFormBuilderController.bind(scopeTemporal, path);
      return scopeTemporal;
    };

    /**
     * Evalua una expressión contra el scope del campo.
     * Ej, para el campo "CAB.EMB.LIN.PRECIO"
     * el `path` es "CAB.EMB.LIN", y evaluara contra un scope con scope.CAB, scope.EMB, scope.LIN
     * -> se usa para evaluar expresiones (como required) en las celdas
     * @param {string} path
     * @param {string} expression
     */
    const parserFn = path => (expression, line) => {
      if (expression === false || expression === 'false') {
        return false;
      }
      if (expression === true || expression === 'true') {
        return true;
      }
      const scopeTmp = bindScope(path); // construimos el scope recursivamente: va crear scope.CAB, scope.EMB pero no el scope.LIN que no conoce
      const model = path.split('.').slice(-1)[0]; // recuperamos 'LIN' de 'CAB.EMB.LIN'
      scopeTmp[model] = line; // añadimos la linea, aqui se crea el scope.LIN
      const context = Object.keys(scopeTmp)
        .filter(p => !p.startsWith('$')) // quitamos las propiedades de angular
        .reduce((acc, cur) => {
          acc[cur] = scopeTmp[cur];
          return acc;
        }, {});
      let res = false;
      try {
        res = parse(context, expression);
      } catch (e) {
        console.error(e);
      }
      return res;
    };

    // binding hecho, recuperamos el modelo en entrada del ec-grid
    // ej. si CAB.TEST.LIN -> TEST.LIN (en salida del ec-grid, current sera LIN)
    const currentModel = attrs.xPath
      .split('.')
      .slice(-2)
      .join('.');

    // lo copiamos en el scope para fb-cell-editor
    scope.xPath = attrs.xPath;
    scope.ecFormBuilderController = ecFormBuilderController;

    ////////////////////////////////////////////////////////////////////////////

    const unbinds = [];
    const watchers = {
      visible: {},
      required: {},
    };
    const gridName = attrs.name;
    const viewProperty = ecFormBuilderController.dispatcher.get(attrs.viewPropertyKey);
    const engineApi = ecFormBuilderController.dispatcher.get(attrs.engineApiKey);

    scope.state = ecFormBuilderController.state ? ecFormBuilderController.state[gridName] : null;

    const stateIndexed = scope.state.reduce((acc, cur) => {
      acc[cur.name] = cur;
      return acc;
    }, {});

    const elem = element[0];
    elem.id = elem.id || `${attrs.xPath}__fbGrid`;

    if (attrs.fbFlash) {
      scope.fbFlash = params => {
        const model = params.model;
        const colKey = findColKey(params.model);
        const [rowIndex, ...indexes] = params.indexes;
        // transformar argumentos para el flash del ec-grid/ec-grid-edit
        eGrid.api.flash({
          colKey,
          rowIndex,
          params: {
            model,
            indexes,
          },
        });
      };
    }

    function findColKey(model) {
      const column = findCol(viewProperty.properties, model);
      if (!column) {
        return '';
      }
      return column.name ?? column.model; // primero devolvemos el name si existe

      function findCol(properties, currentModelToFind) {
        if (!currentModelToFind) {
          return null;
        }
        let found = properties.find(p => p.model === currentModelToFind);
        if (!found) {
          found = findCol(properties, engineApi.utils.getParent(currentModelToFind));
        }
        return found;
      }
    }

    const engineGridApi = {
      parse: parserFn(viewProperty.model), // parse en el scope del LIN
      mapPropertiesToColumns,
    };
    const columns = mapPropertiesToColumns(viewProperty.properties);
    scope.columns = getScopeColumns();
    scope.buttons = getButtons();
    function getButtons() {
      if (!viewProperty.metadata || !viewProperty.metadata.toolbar || !viewProperty.metadata.toolbar.properties) {
        return [];
      }
      return viewProperty.metadata.toolbar.properties;
    }

    // compilamos el template para pintar el grid.
    $compile(
      element
        .append(
          `
          <span id="${elem.id}__label" class="fb-grid--label">${attrs.label || ''}</span>
          <ec-grid
            id="${elem.id}__ecGrid"
            aria-labelledby="${elem.id}__label"
            ng-model="${currentModel}"
            columns="columns"
            state="state"
            current="${attrs.model}"
            ${ecFormBuilderController.options.isLineNumber ? 'show-line-number' : ''}
            ${scope.buttons && scope.buttons.length ? 'buttons="buttons"' : ''}
            selected="selected"
            ${typeof attrs.sortable !== 'undefined' ? `sortable="${attrs.sortable}"` : ''}
            ${typeof attrs.disabled !== 'undefined' ? `disabled` : ''}
            ${typeof attrs.ngDisabled !== 'undefined' ? `ng-disabled="${attrs.ngDisabled}"` : ''}
            ${typeof attrs.toolbar !== 'undefined' ? `toolbar='${attrs.toolbar}'` : ''}
            ${element[0].hasAttribute('readonly') ? ` readonly ` : ''}
            state-without-sort
            >
          </ec-grid>`
        )
        .contents()
    )(scope);

    const eGrid = element[0].querySelector('ec-grid');

    unbinds.push(engineApi.events.on(`ecFormBuilder:fbGrid:${gridName}`, ({ name, hide }) => (hide ? removeColumn(name) : addColumn(name))));
    unbinds.push(
      engineApi.events.on('ecFormBuilder:forceRefresh', () => {
        refreshGrid({
          force: true,
        });
      })
    );
    unbinds.push(
      engineApi.events.on('ecFormBuilder:refresh', () => {
        refreshGrid();
      })
    );
    unbinds.push(engineApi.events.on('ecFormBuilder:fbGrid:lineNumber', onLineNumber));
    eGrid.addEventListener('ecGrid:ready', onReady);
    eGrid.addEventListener('ecGrid:state:dragEnd', onStateChange);
    eGrid.addEventListener('ecGrid:button', onClickButton);

    scope.$on('$destroy', () => {
      unbinds.forEach(unbind => unbind());
      Object.values(watchers.visible || {}).forEach(({ stop }) => stop());
      Object.values(watchers.required || {}).forEach(({ stop }) => stop());
      if (!eGrid) {
        return;
      }
      eGrid.removeEventListener('ecGrid:ready', onReady);
      eGrid.removeEventListener('ecGrid:state:dragEnd', onStateChange);
      eGrid.removeEventListener('ecGrid:button', onClickButton);
    });

    //////////////////////////////////////////////////////////////////////////

    function mapPropertiesToColumns(properties) {
      const tempcolumns = properties.reduce((acc, viewField) => {
        if (viewField.visible === 'false' || viewField.visible === false) {
          return acc;
        } // la columna nunca sera visible, la quitamos definitivamente

        if (isExpression(viewField.visible)) {
          // guardamos el watcher (pero aun no se arranca)
          registerWatcher('visible', viewField.visible, viewField.name, toggleVisibility);
        }

        // @avaron hay que revisar que viewField.required no incluya condiciones de linea, ya que este watcher afecta a toda la columna
        // y si la expresión tiene alguna condición con un campo de la línea, el required dinámico solo debe afectar la celda de la fila
        // @odeniel ademas el watcher solo escucha sobre CAB. Entonces no nos valen expresiones sobre LIN, EMB o cualquier subregistro
        if (isExpression(viewField.required) && !expressionContainsLinCondition(viewField.model, viewField.required)) {
          //guardamos el watcher (pero aun no se arranca)
          registerWatcher('required', viewField.required, viewField.name, setRequiredField);
        }

        // creacion de la columna
        const generateCell = injectorService.cell(viewField.type); // buscamos el generador por el tipo

        if (!generateCell) {
          return acc;
        }

        // @odeniel creamos un parse (o eval) para poder evaluar las expressiones required, visible,...
        // se usa en engine-cells.service.js

        const column = generateCell(viewField, engineApi, engineGridApi);
        acc.push(column); // añadimos la columna al grid

        return acc;
      }, []);

      return tempcolumns;
    }

    function getScopeColumns() {
      return columns.filter(column => {
        if (column.type === 'hidden') {
          return true;
        }
        return stateIndexed[column.name] ? !stateIndexed[column.name].hide : false;
      });
    }

    function updateScopeColumns() {
      scope.columns = getScopeColumns();
      eGrid.api.columns.set(scope.columns);
    }

    function addColumn(name) {
      watchers.visible[name] && watchers.visible[name].start();
      watchers.required[name] && watchers.required[name].start();
      updateState(name, false);
      updateScopeColumns();
    }

    function removeColumn(name) {
      watchers.visible[name] && watchers.visible[name].stop();
      watchers.required[name] && watchers.required[name].stop();
      updateState(name, true);
      updateScopeColumns();
    }

    function updateState(name, hide) {
      stateIndexed[name].hide = hide;
    }

    function dispatchStateToFormBuilder() {
      ecFormBuilderController.udpateStateRegister(gridName, scope.state);
    }

    function hideField(name) {
      eGrid.api.columns.hide(name);
    }

    function showField(name) {
      eGrid.api.columns.show(name);
    }

    function toggleVisibility(newValue, name) {
      return newValue ? showField(name) : hideField(name);
    }

    function setRequiredField(newValue, name) {
      eGrid.api.columns.setRequired(name, newValue);
    }

    function refreshGrid(options) {
      eGrid.api.refresh(options);
    }

    function onClickButton({ detail: { data, selected, button } }) {
      ecFormBuilderController.events.emit('ecFormBuilder:fbGrid:button', { data, selected, button });
    }

    function onLineNumber({ show }) {
      show ? eGrid.api.columns.lineNumber.show() : eGrid.api.columns.lineNumber.hide();
    }

    function onStateChange({ detail: api }) {
      // @odeniel no escuchamos el ecGrid:state porque se dispara para ecGrid:state:visible y ecGrid:state:dragEnd
      // @odeniel no escuchamos el ecGrid:state:visible porque no hace falta, ese evento viene del formbuilder, se trata en addColumn/removeColumn

      const newOrder = getStateIndexOrder(api.columns.state);

      const oldOrder = getStateIndexOrder(scope.state);

      //Se actualizan los parametros de las columnas que se encuentran en ambos estados
      Object.keys(newOrder).forEach(elem => {
        scope.state[oldOrder[elem]] = api.columns.state[newOrder[elem]];
      });

      const newState = sortScopeState(oldOrder, newOrder, api.columns.state.length);

      newState.isTypeGrid = scope.state.isTypeGrid;
      newState.total = newState.length + 2;

      scope.state = newState;

      dispatchStateToFormBuilder();
    }

    function getStateIndexOrder(state) {
      let index = 0;
      return state.reduce((acc, cur) => {
        acc[cur.name] = index++;
        return acc;
      }, {});
    }

    function sortScopeState(oldOrder, newOrder, lastColumn) {
      return Object.keys(oldOrder).reduce((acc, cur) => {
        if (newOrder[cur] === undefined) {
          acc[lastColumn] = scope.state[oldOrder[cur]];
          lastColumn++;
          return acc;
        }
        acc[newOrder[cur]] = scope.state[oldOrder[cur]];
        return acc;
      }, []);
    }

    function onReady() {
      eGrid.removeEventListener('ecGrid:ready', onReady);
      scope.columns.forEach(({ name }) => {
        watchers.visible[name] && watchers.visible[name].start();
        watchers.required[name] && watchers.required[name].start();
      });
    }

    /**
     * @param {string} type - 'visible'|'required'
     */
    function registerWatcher(type, expression, name, cb) {
      // guardamos los watchers para inicializarlos solo cuando el grid este ready
      let unwatch;
      const start = () =>
        (unwatch = ecFormBuilderController.watch(expression, newValue => {
          if (typeof newValue === 'undefined') {
            newValue = false;
          }
          //newValue ? showField(name) : hideField(name)
          cb(newValue, name);
        }));
      const stop = () => unwatch && unwatch();

      watchers[type][name] = {
        start,
        stop,
      };
    }

    function isExpression(condition) {
      return typeof condition === 'string' && condition !== '' && condition !== 'true' && condition !== 'false';
    }

    // comprobamos que la expresión no tiene una condición que dependa de un campo de la linea o cualquier subregistro que no sea CAB
    function expressionContainsLinCondition(path, expression) {
      const array = path.split('.').slice(1, -1); // CAB.EMB.LIN.CAMPO => ['EMB', 'LIN']

      for (let i = 0, len = array.length; i < len; i++) {
        const cur = array[i]; // 'EMB', despues LIN,...
        // si expression empieza por "EMB...." o contiene un "... EMB." o "... EMB["
        if (expression.startsWith(cur) || expression.includes(` ${cur}.`) || expression.includes(` ${cur}[`)) {
          return true;
        }
      }
      return false;
    }
  }
}
